/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';

import betweenWith from '@broadleaf/admin-components/dist/common/utils/lodash/betweenWith';
import FulfillmentItemSummary from '../FulfillmentItemSummary';
import { IFulfillmentViewCommon } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import { IOrderFulfillmentItem } from '@broadleaf/admin-components/dist/types/oms';
import { groupBy, keyBy, map, reduce } from 'lodash';
import { ItemLabeledPrice } from '../FulfillmentItemSummary/FulfillmentItemSummary';

export const Items: React.FC<ItemsProps> = ({ className, state }) => {
  if (!state.data) return null;
  const fulfillment = state.data;
  const itemsByVendorRef = fulfillment.vendorRef
    ? { [fulfillment.vendorRef]: fulfillment.fulfillmentItems }
    : (groupBy(
        fulfillment.fulfillmentItems,
        (fi: IOrderFulfillmentItem) => fi.attributes.vendorRef
      ) as Record<string, Array<IOrderFulfillmentItem>>);
  const vendorInfoByVendorRef = keyBy(
    fulfillment.attributes.fulfillmentSalesInfo.sellerSalesInfos,
    salesInfo => salesInfo.vendorRef
  );

  const keys = Object.keys(itemsByVendorRef);

  return map(keys, vendorRef => {
    const fulfillmentItems = itemsByVendorRef[vendorRef];
    const vendorInfo = vendorInfoByVendorRef[vendorRef];

    const itemsSubtotal = reduce(
      fulfillmentItems,
      function (sum: number, fulfillmentItem: IOrderFulfillmentItem) {
        return sum + fulfillmentItem.merchandiseTotal.amount;
      },
      0
    );

    return (
      <>
        <div className="tw-flex tw-flex-row tw-px-3 tw-py-2">
          <div className="tw-flex-1">
            <h1 className="">Items - {vendorInfo?.accountName}</h1>
          </div>
          <div className="tw-flex-initial tw-px-2">
            <ItemLabeledPrice
              className="tw-text-sm"
              label={'Items Subtotal'}
              price={{ amount: itemsSubtotal, currency: fulfillment.currency }}
            />
          </div>
        </div>
        <hr className="tw-my-1 tw-flex-initial tw-border-gray-300" />

        <div className={className}>
          {betweenWith(
            fulfillmentItems.map(fulfillmentItem => (
              <FulfillmentItemSummary
                key={fulfillmentItem.id}
                className={className}
                fulfillmentItem={fulfillmentItem}
              />
            )),
            (_, ___, index) => (
              <hr key={`hr-${index}`} className="tw-my-1 tw-border-gray-300" />
            )
          )}
        </div>
      </>
    );
  });
};

export interface ItemsProps extends IFulfillmentViewCommon {
  className?: string;
}

export default Items;
